// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// mediaCache keeps a LOCAL copy of (decrypted) media on the device so a blob that the
// server later garbage-collects — or that's simply offline — still renders in history.
// Media is E2E, so the device already decrypts it to display; we cache those decrypted
// bytes (same trust posture as saving to the photo gallery), keyed by the content-hash
// media id. Two backends:
//   • web/desktop → OPFS for the bytes + a small IndexedDB index, with a self-managed
//     LRU cap (we evict the oldest, never let the browser silently drop things);
//   • native (Capacitor) → the filesystem under a "Zalmoxis/" folder (unbounded vs the
//     WebView quota, and survives a web-storage wipe).
import { isNativeApp } from "./server";

const CAP_KEY = "zalmoxis.mediacache.cap";
const DEFAULT_CAP = 1_000_000_000; // 1 GB
const ZALMOXIS_DIR = "Zalmoxis";

export function cacheCapBytes(): number {
  return Number(localStorage.getItem(CAP_KEY)) || DEFAULT_CAP;
}
export function setCacheCapBytes(bytes: number): void {
  localStorage.setItem(CAP_KEY, String(Math.max(0, Math.floor(bytes))));
}

const AUTO_KEY = "zalmoxis.mediacache.autodownload";
// autoDownloadEnabled reports whether media is cached proactively on receive (default
// on) vs only when viewed.
export function autoDownloadEnabled(): boolean {
  return localStorage.getItem(AUTO_KEY) !== "off";
}
export function setAutoDownload(on: boolean): void {
  localStorage.setItem(AUTO_KEY, on ? "on" : "off");
}

// isCacheableMedia reports whether a (decrypted-or-plaintext) content is a downloadable
// media attachment.
export function isCacheableMedia(content: Record<string, unknown>): boolean {
  const file = content.file as { key?: string } | undefined;
  return typeof content.url === "string" && !!file?.key && ["m.image", "m.video", "m.audio", "m.file"].includes(String(content.msgtype));
}

// ---- IndexedDB index (id -> {size, lastAccess, mimetype}) ------------------

interface IndexItem {
  id: string;
  size: number;
  lastAccess: number;
  mimetype: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;
function db(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open("zalmoxis-media", 1);
    req.onupgradeneeded = () => req.result.createObjectStore("items", { keyPath: "id" });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function idbReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function items(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return (await db()).transaction("items", mode).objectStore("items");
}

async function idxGet(id: string): Promise<IndexItem | undefined> {
  return idbReq((await items("readonly")).get(id) as IDBRequest<IndexItem | undefined>);
}
async function idxPut(item: IndexItem): Promise<void> {
  await idbReq((await items("readwrite")).put(item));
}
async function idxDelete(id: string): Promise<void> {
  await idbReq((await items("readwrite")).delete(id));
}
async function idxAll(): Promise<IndexItem[]> {
  return idbReq((await items("readonly")).getAll() as IDBRequest<IndexItem[]>);
}
async function idxClear(): Promise<void> {
  await idbReq((await items("readwrite")).clear());
}

// ---- OPFS blob store (web) -------------------------------------------------

async function opfsDir(): Promise<FileSystemDirectoryHandle> {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle("media", { create: true });
}
async function opfsGet(id: string): Promise<Uint8Array | null> {
  try {
    const fh = await (await opfsDir()).getFileHandle(id);
    return new Uint8Array(await (await fh.getFile()).arrayBuffer());
  } catch {
    return null;
  }
}
async function opfsPut(id: string, bytes: Uint8Array): Promise<void> {
  const fh = await (await opfsDir()).getFileHandle(id, { create: true });
  const w = await fh.createWritable();
  await w.write(bytes as BufferSource);
  await w.close();
}
async function opfsDelete(id: string): Promise<void> {
  try {
    await (await opfsDir()).removeEntry(id);
  } catch {
    /* already gone */
  }
}

// evictIfNeeded enforces the LRU cap: while the indexed total exceeds the cap (or
// `aggressive` halves it to make room after a QuotaExceededError), drop the
// least-recently-accessed entries.
async function evictIfNeeded(aggressive = false): Promise<void> {
  const items = (await idxAll()).sort((a, b) => a.lastAccess - b.lastAccess);
  let total = items.reduce((n, i) => n + i.size, 0);
  const cap = aggressive ? cacheCapBytes() / 2 : cacheCapBytes();
  for (const it of items) {
    if (total <= cap) break;
    await opfsDelete(it.id);
    await idxDelete(it.id);
    total -= it.size;
  }
}

// ---- native filesystem store (Capacitor) -----------------------------------

// A minimal view of the @capacitor/filesystem API. Loaded via a computed specifier so
// the web build neither type-checks nor bundles it; the native build resolves it.
interface FilesystemPlugin {
  writeFile(o: { path: string; data: string; directory: string; recursive?: boolean }): Promise<{ uri: string }>;
  readFile(o: { path: string; directory: string }): Promise<{ data: string }>;
  deleteFile(o: { path: string; directory: string }): Promise<void>;
  mkdir(o: { path: string; directory: string; recursive?: boolean }): Promise<void>;
}
type FsModule = { Filesystem: FilesystemPlugin; Directory: { Documents: string } };

let fsPromise: Promise<FsModule | null> | null = null;
function fs(): Promise<FsModule | null> {
  if (fsPromise) return fsPromise;
  fsPromise = (async () => {
    try {
      return (await import("@capacitor/filesystem")) as unknown as FsModule;
    } catch {
      return null;
    }
  })();
  return fsPromise;
}

function bytesToB64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}
function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function nativeGet(id: string): Promise<Uint8Array | null> {
  const mod = await fs();
  if (!mod) return null;
  try {
    const { data } = await mod.Filesystem.readFile({ path: `${ZALMOXIS_DIR}/${id}`, directory: mod.Directory.Documents });
    return b64ToBytes(data);
  } catch {
    return null;
  }
}
async function nativePut(id: string, bytes: Uint8Array, mimetype: string): Promise<void> {
  const b64 = bytesToB64(bytes);
  const mod = await fs();
  if (mod) {
    try {
      await mod.Filesystem.writeFile({ path: `${ZALMOXIS_DIR}/${id}`, data: b64, directory: mod.Directory.Documents, recursive: true });
    } catch {
      /* best-effort */
    }
  }
  // Images/videos also land in the gallery (MediaStore) under a Zalmoxis album.
  if (mimetype.startsWith("image/") || mimetype.startsWith("video/")) {
    try {
      const ext = mimetype.split("/")[1] || "bin";
      const { Gallery } = await import("capacitor-gallery");
      await Gallery.save({ data: b64, filename: `${id.slice(0, 16)}.${ext}`, mimetype });
    } catch {
      /* gallery plugin absent or denied — the Zalmoxis folder copy still exists */
    }
  }
}

// ---- public API ------------------------------------------------------------

// getMedia returns cached decrypted bytes for a media id, or null on a miss.
export async function getMedia(id: string): Promise<Uint8Array | null> {
  if (!id) return null;
  if (isNativeApp()) return nativeGet(id);
  const bytes = await opfsGet(id);
  if (bytes) {
    const it = await idxGet(id);
    if (it) void idxPut({ ...it, lastAccess: Date.now() }); // touch for LRU
  }
  return bytes;
}

// putMedia caches decrypted bytes for a media id (best-effort; enforces the LRU cap on
// web). Called after a server fetch+decrypt so the next view — even offline, or after
// the server GCs the blob — is served locally.
export async function putMedia(id: string, bytes: Uint8Array, mimetype: string): Promise<void> {
  if (!id || bytes.byteLength === 0) return;
  if (isNativeApp()) {
    await nativePut(id, bytes, mimetype);
    return;
  }
  try {
    void navigator.storage?.persist?.(); // exempt from automatic eviction (best-effort)
    await opfsPut(id, bytes);
    await idxPut({ id, size: bytes.byteLength, lastAccess: Date.now(), mimetype });
    await evictIfNeeded();
  } catch {
    // Likely QuotaExceededError — make room and retry once.
    try {
      await evictIfNeeded(true);
      await opfsPut(id, bytes);
      await idxPut({ id, size: bytes.byteLength, lastAccess: Date.now(), mimetype });
    } catch {
      /* give up — the server fetch path still rendered it */
    }
  }
}

// cacheStats reports local cache usage (web only; native is device-managed).
export async function cacheStats(): Promise<{ count: number; bytes: number; quota: number }> {
  if (isNativeApp()) return { count: 0, bytes: 0, quota: 0 };
  const items = await idxAll();
  const est = (await navigator.storage?.estimate?.()) ?? {};
  return { count: items.length, bytes: items.reduce((n, i) => n + i.size, 0), quota: est.quota ?? 0 };
}

// clearCache drops every locally-cached blob.
export async function clearCache(): Promise<void> {
  if (isNativeApp()) return; // device-managed (Files / gallery)
  for (const it of await idxAll()) await opfsDelete(it.id);
  await idxClear();
}
